import React, { useState, useEffect, useCallback } from 'react';
import { userImageService, UserImage } from '../lib/userImageService';
import { useAuth } from '../contexts/AuthContext';
import { ImageOptimizer } from '../lib/imageOptimizer';
import { ImageErrorHandler } from './ImageErrorHandler';

interface ImageSelectorProps {
  onImageSelected: (file: File, source: 'upload' | 'existing', imageData?: UserImage) => void;
  onImageRemoved: () => void;
  acceptedFormats?: string;
  maxSizeMB?: number;
  selectedImage?: File | null;
  showExisting?: boolean;
  title?: string;
  uploadButtonText?: string;
}

export const ImageSelector: React.FC<ImageSelectorProps> = ({
  onImageSelected,
  onImageRemoved,
  acceptedFormats = "image/*",
  maxSizeMB = 50,
  selectedImage,
  showExisting = true,
  title = "Select Image",
  uploadButtonText = "Upload New Image"
}) => {
  const { user } = useAuth();
  const [mode, setMode] = useState<'upload' | 'existing'>('upload');
  const [userImages, setUserImages] = useState<UserImage[]>([]);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [selectedExistingImage, setSelectedExistingImage] = useState<UserImage | null>(null);
  const [filterService, setFilterService] = useState<'all' | 'moodboard' | 'upstaging' | 'touchup' | 'gallery' | 'furniture'>('all');
  const [imageError, setImageError] = useState<Error | null>(null);
  const [errorImageUrl, setErrorImageUrl] = useState<string>('');

  // Load user's existing images
  const loadUserImages = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoadingImages(true);
    try {
      // Debug multiple images setup
      await userImageService.debugMultipleImagesSetup(user.id);
      
      const images = await userImageService.getUserImages(user.id);
      setUserImages(images);
    } catch (error) {
      console.error('Failed to load user images:', error);
    } finally {
      setIsLoadingImages(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && showExisting && mode === 'existing') {
      loadUserImages();
    }
  }, [user?.id, mode, showExisting, loadUserImages]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ImageOptimizer.isValidImageFile(file)) {
      alert('Unsupported file format. Please upload JPG, JPEG, PNG, HEIC, or HEIF images.');
      return;
    }

    // Validate file size
    if (!ImageOptimizer.validateFileSize(file, maxSizeMB)) {
      alert(`File size too large. Please upload images smaller than ${maxSizeMB}MB.`);
      return;
    }

    onImageSelected(file, 'upload');
  };

  const handleExistingImageSelect = async (imageData: UserImage) => {
    setSelectedExistingImage(imageData);
    
    try {
      console.log('🖼️ Loading existing image:', imageData.description, 'URL:', imageData.url);
      
      // Use the new selectExistingImage function with CORS workaround
      const result = await userImageService.selectExistingImage(imageData);
      
      if (result.success) {
        console.log('✅ Successfully loaded image file:', result.file.name, 'Size:', result.file.size);
        onImageSelected(result.file, 'existing', imageData);
      } else {
        throw new Error(result.error || 'Failed to load existing image');
      }
    } catch (error) {
      console.error('❌ Failed to load selected image:', error);
      setSelectedExistingImage(null); // Reset selection on error
      
      // Set error state for the error handler component
      setImageError(error instanceof Error ? error : new Error('Unknown error occurred'));
      setErrorImageUrl(imageData.url);
    }
  };

  const handleRemoveImage = () => {
    setSelectedExistingImage(null);
    onImageRemoved();
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleErrorClose = () => {
    setImageError(null);
    setErrorImageUrl('');
  };

  const handleErrorRetry = async () => {
    if (selectedExistingImage) {
      try {
        const result = await userImageService.selectExistingImage(selectedExistingImage);
        
        if (result.success) {
          onImageSelected(result.file, 'existing', selectedExistingImage);
          setImageError(null);
          setErrorImageUrl('');
        } else {
          throw new Error(result.error || 'Failed to load existing image');
        }
      } catch (error) {
        // Error will be handled by the existing error handling
        console.error('Retry failed:', error);
      }
    }
  };

  const handleUploadInstead = () => {
    setMode('upload');
    setImageError(null);
    setErrorImageUrl('');
  };

  const filteredImages = userImages.filter(image => {
    // Exclude original images from AI touchup and upstaging - only show generated results
    if (image.type === 'upstaging-original' || image.type === 'touchup-original') {
      return false;
    }
    
    // Apply service type filter
    return filterService === 'all' || image.serviceType === filterService;
  });

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      
      {/* Mode selector */}
      {showExisting && (
        <div className="flex space-x-4 mb-4">
          <button
            onClick={() => setMode('upload')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              mode === 'upload'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Upload New
          </button>
          <button
            onClick={() => setMode('existing')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              mode === 'existing'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Use Existing
          </button>
        </div>
      )}

      {/* Upload mode */}
      {mode === 'upload' && (
        <div className="space-y-4">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              accept={acceptedFormats}
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer flex flex-col items-center space-y-2"
            >
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-lg font-medium text-gray-600">{uploadButtonText}</span>
              <span className="text-sm text-gray-500">
                Support: JPG, JPEG, PNG, HEIC, HEIF (Max {maxSizeMB}MB)
              </span>
            </label>
          </div>
          
          {selectedImage && mode === 'upload' && (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-800">{selectedImage.name}</span>
              </div>
              <button
                onClick={handleRemoveImage}
                className="text-red-600 hover:text-red-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Existing images mode */}
      {mode === 'existing' && showExisting && (
        <div className="space-y-4">
          {/* Service filter */}
          <div className="flex flex-wrap gap-2">
            {['all', 'gallery', 'moodboard', 'upstaging', 'touchup', 'furniture'].map((service) => (
              <button
                key={service}
                onClick={() => setFilterService(service as typeof filterService)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  filterService === service
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {service === 'all' ? 'All' : service === 'gallery' ? 'My Uploads' : service === 'furniture' ? 'Furniture' : service.charAt(0).toUpperCase() + service.slice(1)}
              </button>
            ))}
          </div>

          {/* Selected existing image indicator */}
          {selectedExistingImage && (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="text-green-800">Selected: {selectedExistingImage.description}</span>
              </div>
              <button
                onClick={handleRemoveImage}
                className="text-red-600 hover:text-red-800 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Images grid */}
          {isLoadingImages ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading your images...</p>
            </div>
          ) : filteredImages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p>No images found{filterService !== 'all' ? ` in ${filterService}` : ''}.</p>
              <p className="text-sm">Generate some content first, then return here to reuse your images!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
              {filteredImages.map((image) => (
                <div
                  key={image.id}
                  onClick={() => handleExistingImageSelect(image)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 hover:shadow-lg ${
                    selectedExistingImage?.id === image.id
                      ? 'border-blue-600 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.description}
                    className="w-full h-24 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-1 left-1 right-1">
                    <p className="text-white text-xs font-medium truncate">
                      {image.description}
                    </p>
                    <p className="text-white/80 text-xs">
                      {new Date(image.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  {selectedExistingImage?.id === image.id && (
                    <div className="absolute top-1 right-1">
                      <svg className="w-5 h-5 text-blue-600 bg-white rounded-full p-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Error Handler */}
      {imageError && (
        <ImageErrorHandler
          error={imageError}
          imageUrl={errorImageUrl}
          onRetry={handleErrorRetry}
          onUploadInstead={handleUploadInstead}
          onClose={handleErrorClose}
        />
      )}
    </div>
  );
}; 